(() => {
  if (customElements.get('hero-slideshow')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      // The page can re-attach the banner (e.g. when the header area is moved); set up only once
      if (this.ready) {
        this.start();
        return;
      }
      this.ready = true;

      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.index = 0;
      this.timer = null;
      this.hovering = false;
      this.focused = false;
      this.editorPaused = false;

      // One slide: leave it as a still banner
      if (this.slides.length < 2) {
        this.classList.add('hero-slideshow--single');
        return;
      }

      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 5000;

      // Slide mode loops in one direction: a copy of the first slide sits after the last,
      // so going last → first keeps moving forward instead of rewinding past every slide
      this.looping = this.classList.contains('hero-slideshow--slide');
      this.pos = 0;
      this.moving = false;
      if (this.looping) {
        const clone = this.slides[0].cloneNode(true);
        clone.removeAttribute('data-slide');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        this.track.appendChild(clone);
      }

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot), true)));

      // Pause while a mouse is over the banner (taps on phones don't count as hovering)
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovering = true;
        this.stop();
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovering = false;
        this.start();
      });
      // Pause for keyboard users only; tapping a dot or arrow shouldn't stop it for good
      this.addEventListener('focusin', (event) => {
        if (!event.target.matches(':focus-visible')) return;
        this.focused = true;
        this.stop();
      });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.start();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (event.key === 'ArrowRight') this.go(this.index + 1, true);
      });

      this.bindSwipe();

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the slide being edited and hold it there
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.findIndex((slide) => slide.contains(event.target));
        if (i < 0) return;
        this.editorPaused = true;
        this.go(i);
        this.stop();
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.editorPaused = false;
        this.start();
      });

      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bindSwipe() {
      let startX = null;
      let startY = null;

      this.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          this.stop();
        },
        { passive: true }
      );

      this.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.go(dx < 0 ? this.index + 1 : this.index - 1, true);
        } else {
          this.start();
        }
      });
    }

    go(target, fromUser = false) {
      const count = this.slides.length;
      if (this.moving) return;

      if (this.looping) {
        let pos = target;
        if (target < 0) {
          // Back from the first slide: jump unseen to the copy of slide 1, then slide back to the last
          this.jump(count);
          pos = count - 1;
        } else if (target >= count) {
          pos = count; // forward onto the copy of slide 1
        }
        this.pos = pos;
        this.moving = true;
        // Once the move finishes, swap the copy for the real first slide without animating
        setTimeout(() => {
          this.moving = false;
          if (this.pos === count) this.jump(0);
        }, 850);
      }

      this.index = (target + count) % count;
      this.update();
      if (fromUser) this.restart();
    }

    // Move the track instantly, with no slide animation
    jump(pos) {
      this.pos = pos;
      this.track.style.transition = 'none';
      this.style.setProperty('--hero-slideshow-index', pos);
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }

    update() {
      this.style.setProperty('--hero-slideshow-index', this.looping ? this.pos : this.index);

      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-label', `${i + 1} of ${this.slides.length}`);
        // Hidden slides stay out of the tab order and away from screen readers
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });

      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    canAutoplay() {
      return (
        this.autoplay &&
        !reducedMotion.matches &&
        !this.hovering &&
        !this.focused &&
        !this.editorPaused &&
        !document.hidden
      );
    }

    start() {
      this.stop();
      if (!this.canAutoplay()) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
})();